import { useState, useEffect } from "react";
import { useLenis } from "lenis/react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, X, Phone } from "lucide-react";

const LINKS = [
  { label: "Storia", href: "#storia" },
  { label: "Menù", href: "#menu" },
  { label: "Servizi", href: "#servizi" },
  { label: "Recensioni", href: "#recensioni" },
  { label: "Contatti", href: "#contatti" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    lenis?.scrollTo(href, { offset: -70, duration: 1.4 });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-[80] border-b transition-[background-color,border-color] duration-500 ${
        scrolled || open
          ? "bg-ink/70 backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-[70px] flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/logo.png"
            alt="Pizzeria Grano Fiumefreddo"
            className="h-12 w-12 rounded-full ring-1 ring-cream/20 group-hover:ring-gold/60 group-hover:scale-105 transition-[transform,box-shadow] duration-300"
          />
          <span className="hidden sm:block text-[10px] tracking-[0.35em] uppercase text-cream/60">
            Pizzeria · Fiumefreddo
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-10" data-testid="header-nav">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace("ù", "u")}`}
              onClick={() => go(l.href)}
              className="link-underline text-xs tracking-[0.25em] uppercase text-cream/70 hover:text-cream transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-phone"
            href="tel:+393892472195"
            className="flex items-center gap-2 bg-terra text-cream text-xs tracking-[0.2em] uppercase px-6 py-3 hover:bg-cream hover:text-ink transition-colors duration-300"
          >
            <Phone size={13} strokeWidth={1.5} />
            389 247 2195
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-cream p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <MenuIcon size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace("ù", "u")}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-3xl text-cream/80 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="mobile-nav-phone"
                href="tel:+393892472195"
                className="text-gold text-sm tracking-[0.2em] uppercase pt-2"
              >
                Chiama · 389 247 2195
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
